// Minimal GIF89a / GIF87a decoder.
// Returns fully composited RGBA frames with their display durations.

export interface GifFrame {
  /** RGBA pixel data, composited, width × height × 4 bytes */
  pixels: Uint8ClampedArray;
  /** Display duration in milliseconds (0-delay frames use 100 ms fallback) */
  delay: number;
}

export interface ParsedGif {
  width: number;
  height: number;
  frames: GifFrame[];
}

function r16(d: Uint8Array, i: number): number {
  return d[i] | (d[i + 1] << 8);
}

function lzwDecode(minCodeSize: number, data: Uint8Array, maxOutput: number): Uint8Array {
  const clear = 1 << minCodeSize;
  const eoi = clear + 1;

  // Code table: each entry is an array of palette indices
  const table: number[][] = [];
  const resetTable = () => {
    table.length = 0;
    for (let i = 0; i <= eoi; i++) table.push([i]);
  };
  resetTable();

  let codeSize = minCodeSize + 1;
  let bitBuf = 0;
  let bitLen = 0;
  let pos = 0;

  const readCode = (): number => {
    while (bitLen < codeSize && pos < data.length) {
      bitBuf |= data[pos++] << bitLen;
      bitLen += 8;
    }
    if (bitLen < codeSize) return eoi;
    const v = bitBuf & ((1 << codeSize) - 1);
    bitBuf >>>= codeSize;
    bitLen -= codeSize;
    return v;
  };

  // Pre-allocate output to avoid double-buffering through a number[]
  const output = new Uint8Array(maxOutput);
  let outIdx = 0;
  let prev: number[] | null = null;

  while (pos <= data.length && outIdx < maxOutput) {
    const code = readCode();
    if (code === clear) {
      resetTable();
      codeSize = minCodeSize + 1;
      prev = null;
      continue;
    }
    if (code === eoi) break;

    let entry: number[];
    if (code < table.length) {
      entry = table[code];
    } else if (code === table.length && prev !== null) {
      // Special case: code not yet in table
      entry = [...prev, prev[0]];
    } else {
      break; // corrupted stream
    }

    for (const b of entry) { if (outIdx < maxOutput) output[outIdx++] = b; }

    if (prev !== null) {
      table.push([...prev, entry[0]]);
      if (table.length === (1 << codeSize) && codeSize < 12) codeSize++;
    }
    prev = entry;
  }

  return output.subarray(0, outIdx);
}

export function decodeGif(buffer: ArrayBuffer): ParsedGif {
  const d = new Uint8Array(buffer);
  let p = 6; // skip "GIF87a" / "GIF89a"

  const W = r16(d, p);
  const H = r16(d, p + 2);
  const packed = d[p + 4];
  const bgIdx = d[p + 5];
  p += 7;

  const hasGCT = (packed >> 7) & 1;
  const gctLen = hasGCT ? 3 << ((packed & 7) + 1) : 0;
  const gct = hasGCT ? d.subarray(p, p + gctLen) : null;
  p += gctLen;

  const frames: GifFrame[] = [];

  // Composited canvas, RGBA
  const canvas = new Uint8ClampedArray(W * H * 4);
  // Saved canvas for disposal method 3 (restore to previous)
  let saved: Uint8ClampedArray | null = null;

  // Previous frame's disposal state (applied before drawing the next frame)
  let prevDisposal = 0;
  let prevFx = 0; let prevFy = 0; let prevFw = W; let
prevFh = H;

  // Current Graphic Control Extension state
  let delay = 100;
  let transIdx = -1;
  let disposal = 0;

  function applyPrevDisposal() {
    if (prevDisposal === 2) {
      // Restore the previous frame's region to the background color
      const bg = gct && bgIdx >= 0
        ? [gct[bgIdx * 3], gct[bgIdx * 3 + 1], gct[bgIdx * 3 + 2], 255]
        : [0, 0, 0, 0];
      for (let y = prevFy; y < prevFy + prevFh; y++) {
        for (let x = prevFx; x < prevFx + prevFw; x++) {
          const ci = (y * W + x) * 4;
          canvas[ci] = bg[0]; canvas[ci + 1] = bg[1];
          canvas[ci + 2] = bg[2]; canvas[ci + 3] = bg[3];
        }
      }
    } else if (prevDisposal === 3 && saved) {
      canvas.set(saved);
    }
  }

  while (p < d.length) {
    const block = d[p++];
    if (block === 0x3B) break; // Trailer

    if (block === 0x21) { // Extension Introducer
      const label = d[p++];
      if (label === 0xF9) { // Graphic Control Extension
        p++; // block size (always 4)
        const f = d[p++];
        delay = r16(d, p) * 10; p += 2; // centiseconds → ms
        transIdx = (f & 1) ? d[p] : -1;
        p++; // transparent color index
        p++; // block terminator
        disposal = (f >> 3) & 7;
      } else {
        // Skip all other extensions via sub-blocks
        while (d[p]) { p += d[p] + 1; }
        p++; // block terminator
      }
      continue;
    }

    if (block === 0x2C) { // Image Descriptor
      const fx = r16(d, p); const
fy = r16(d, p + 2);
      const fw = r16(d, p + 4); const
fh = r16(d, p + 6);
      const iPacked = d[p + 8]; p += 9;

      const interlaced = !!(iPacked & 0x40);
      const hasLCT = (iPacked >> 7) & 1;
      const lctLen = hasLCT ? 3 << ((iPacked & 7) + 1) : 0;
      const lct = hasLCT ? d.subarray(p, p + lctLen) : null;
      p += lctLen;

      const ct = lct || gct;
      const minCode = d[p++];

      // Collect LZW sub-blocks — two-pass to avoid intermediate number[] allocation
      let totalBytes = 0;
      let scan = p;
      while (d[scan]) { totalBytes += d[scan]; scan += d[scan] + 1; }
      const blocks = new Uint8Array(totalBytes);
      let blockOut = 0;
      while (d[p]) {
        const len = d[p++];
        blocks.set(d.subarray(p, p + len), blockOut);
        blockOut += len;
        p += len;
      }
      p++; // block terminator

      const indices = lzwDecode(minCode, blocks, fw * fh);

      // Apply the previous frame's disposal method
      applyPrevDisposal();

      // Save canvas state before drawing (for disposal method 3 of the next frame)
      saved = canvas.slice();

      // Build row-order array for interlaced GIFs
      let rows: number[];
      if (interlaced) {
        rows = [];
        const passes = [[0, 8], [4, 8], [2, 4], [1, 2]] as const;
        for (const [start, step] of passes) {
          for (let r = start; r < fh; r += step) rows.push(r);
        }
      } else {
        rows = Array.from({ length: fh }, (_, i) => i);
      }

      // Composite frame pixels onto canvas
      if (ct) {
        for (let dataRow = 0; dataRow < fh; dataRow++) {
          const visRow = rows[dataRow];
          for (let col = 0; col < fw; col++) {
            const idx = indices[dataRow * fw + col];
            if (idx === transIdx) continue; // transparent pixel
            const ci = ((fy + visRow) * W + (fx + col)) * 4;
            canvas[ci] = ct[idx * 3];
            canvas[ci + 1] = ct[idx * 3 + 1];
            canvas[ci + 2] = ct[idx * 3 + 2];
            canvas[ci + 3] = 255;
          }
        }
      }

      frames.push({
 pixels: canvas.slice(),
delay: delay || 100,
});

      prevDisposal = disposal;
      prevFx = fx; prevFy = fy; prevFw = fw; prevFh = fh;
      delay = 100; transIdx = -1; disposal = 0;
      continue;
    }

    break; // Unknown block — stop
  }

  return {
 width: W,
height: H,
frames,
};
}
